import React, { useState } from 'react';
import { Organization } from './types/organization';
import { LanguageProvider } from './LanguageContext';
import { AuthProvider } from './contexts/AuthContext';
import RoutesTree from './components/RoutesTree';
import PageWidthLayout from './components/PageWidthLayout';
import ResetPasswordPage from './views/auth/login/password_reset/ResetPasswordPage';
import InviteAcceptPage from './views/auth/invite/InviteAcceptPage';
import { EffortProvider } from './contexts/EffortContext';
import { SharedDataProvider } from './contexts/SharedDataContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import NewVersionBanner from './components/NewVersionBanner';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
// Helper to render public pages based on the URL
const getPublicPage = () => {
  const path = window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  // Add more public pages here as needed
  if (path === '/auth/login/password_reset' && params.get('token')) {
    return <ResetPasswordPage />;
  }
  if (path === '/auth/invite/accept' && params.get('token')) {
    return <InviteAcceptPage />;
  }
  if (path === '/auth/invite/accept/sso') {
    return <InviteAcceptPage isSSO={true} />;
  }
  // Example for future public pages:
  // if (path === '/some/other/public/page') {
  //   return <SomeOtherPublicPage />;
  // }
  return null;
};

const App = () => {
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [avatarEl, setAvatarEl] = useState<null | HTMLElement>(null);

  const handleClose = () => {
    setAnchorEl(null);
    setAvatarEl(null);
  };

  const handleOrgSelect = (org: Organization) => {
    setSelectedOrg(org);
    handleClose();
  };

  // Render public page if matched, otherwise render the main app
  const publicPage = getPublicPage();
  const content = publicPage ? (
    publicPage
  ) : (
    <AuthProvider>
      <PermissionsProvider>
        <LanguageProvider>
          <PageWidthLayout>
            <SharedDataProvider>
              <EffortProvider>
                <RoutesTree
                  isLoggedIn={true}
                  selectedOrg={selectedOrg}
                  setSelectedOrg={setSelectedOrg}
                  anchorEl={anchorEl}
                  avatarEl={avatarEl}
                  setAnchorEl={setAnchorEl}
                  setAvatarEl={setAvatarEl}
                  handleClose={handleClose}
                  handleOrgSelect={handleOrgSelect}
                />
              </EffortProvider>
            </SharedDataProvider>
          </PageWidthLayout>
        </LanguageProvider>
      </PermissionsProvider>
    </AuthProvider>
  );

  return (
    <ChunkErrorBoundary>
      <NewVersionBanner />
      {content}
    </ChunkErrorBoundary>
  );
};

export default App;
